/**
 * Stateless rosters card grid — usable inside a route page or inside a Modal
 * opened from the SideNav. Reads from the snapshot passed by the parent (so
 * the parent owns the SSE stream, not this component).
 */
import { useState } from 'react';
import PlayerAvatar from './PlayerAvatar';
import PlayerModal, { type PlayerSeed } from './PlayerModal';
import { selectRibbon } from '../state/selectors';
import { teamColor } from '../state/teamColors';
import styles from '../pages/Rosters.module.css';

interface RosterRow {
  id: number;
  name: string;
  jersey: string | null;
  position: string | null;
  isStarter: boolean;
}

function rosterForSide(
  snapshot: Record<string, unknown> | null,
  side: 'away' | 'home',
): RosterRow[] {
  if (!snapshot) return [];
  const live = (snapshot.liveData ?? {}) as Record<string, unknown>;
  const box = (live.boxscore ?? {}) as Record<string, unknown>;
  const teams = (box.teams ?? {}) as Record<string, unknown>;
  const team = (teams[side] ?? {}) as Record<string, unknown>;
  const battersInOrder: number[] = Array.isArray(team.battingOrder)
    ? (team.battingOrder as unknown[]).map((x) => Number(x)).filter((n) => n > 0)
    : [];
  const players = (team.players ?? {}) as Record<string, unknown>;
  const out: RosterRow[] = [];
  for (const key of Object.keys(players)) {
    const p = (players[key] ?? {}) as Record<string, unknown>;
    const person = (p.person ?? {}) as Record<string, unknown>;
    const id = Number(person.id ?? 0);
    if (!id) continue;
    const position = (p.position ?? {}) as Record<string, unknown>;
    out.push({
      id,
      name: (person.fullName as string) ?? `Player ${id}`,
      jersey: typeof p.jerseyNumber === 'string' ? p.jerseyNumber : null,
      position: (position.abbreviation as string) ?? null,
      isStarter: battersInOrder.includes(id),
    });
  }
  out.sort((a, b) => Number(b.isStarter) - Number(a.isStarter) || a.name.localeCompare(b.name));
  return out;
}

interface RostersPanelProps {
  snapshot: Record<string, unknown> | null;
  gamePk?: number;
}

export default function RostersPanel({ snapshot, gamePk }: RostersPanelProps): JSX.Element {
  const ribbon = selectRibbon(snapshot);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSeed | null>(null);

  if (!ribbon) {
    return <p data-testid="rosters-panel-loading">Loading…</p>;
  }

  return (
    <>
      <div className={styles.twoCol}>
        {(['away', 'home'] as const).map((side) => {
          const teamCode = side === 'away' ? ribbon.awayTeamCode : ribbon.homeTeamCode;
          const abbr = side === 'away' ? ribbon.awayAbbr : ribbon.homeAbbr;
          const { primary } = teamColor(teamCode);
          const rows = rosterForSide(snapshot, side);
          return (
            <section
              key={side}
              className={styles.section}
              aria-label={`${abbr} roster`}
              style={{ '--team-color': primary } as React.CSSProperties}
            >
              <h2
                className={styles.sectionHeading}
                style={{ background: primary }}
                data-testid={`roster-heading-${side}`}
              >
                <span>{abbr}</span>
                <span className={styles.rosterCount}>{rows.length} players</span>
              </h2>
              <ul className={styles.cards}>
                {rows.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`roster-row-${side}-${r.id}`}
                    className={`${styles.card} ${r.isStarter ? styles.cardStarter : ''}`}
                  >
                    <button
                      type="button"
                      className={styles.cardButton}
                      onClick={() =>
                        setSelectedPlayer({
                          id: r.id,
                          name: r.name,
                          jersey: r.jersey,
                          position: r.position,
                        })
                      }
                      aria-label={`Open profile for ${r.name}`}
                    >
                      <PlayerAvatar
                        personId={r.id}
                        name={r.name}
                        size={240}
                        displaySize={48}
                        className={styles.cardAvatar}
                      />
                      <div className={styles.cardBody}>
                        <div className={styles.cardName}>{r.name}</div>
                        <div className={styles.cardMeta}>
                          {r.position && <span className={styles.cardPos}>{r.position}</span>}
                          {r.jersey && <span className={styles.cardJersey}>#{r.jersey}</span>}
                          {r.isStarter && <span className={styles.cardStarterBadge}>Starting</span>}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      <PlayerModal
        gamePk={gamePk}
        playerId={selectedPlayer?.id ?? null}
        seed={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
      />
    </>
  );
}
